import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import { Eye, EyeOff, Key, PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";

type InsertSecretModalProps = {
  availableSecrets: string[];
  onInsertSecret: (secretName: string) => void;
  onCreateAndInsert: (secretName: string, secretValue: string) => void;
  disabled?: boolean;
  triggerOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const InsertSecretModal = ({
  availableSecrets,
  onInsertSecret,
  onCreateAndInsert,
  disabled = false,
  triggerOpen = false,
  onOpenChange,
}: InsertSecretModalProps) => {
  const [newSecretName, setNewSecretName] = useState("");
  const [newSecretValue, setNewSecretValue] = useState("");
  const [showNewSecretValue, setShowNewSecretValue] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (triggerOpen) {
      setOpen(true);
      if (onOpenChange) {
        onOpenChange(false);
      }
    }
  }, [triggerOpen, onOpenChange]);

  const handleReset = () => {
    setNewSecretName("");
    setNewSecretValue("");
    setShowNewSecretValue(false);
  };

  const handleCreateAndInsert = () => {
    if (!newSecretName.trim()) {
      return;
    }
    onCreateAndInsert(newSecretName.trim(), newSecretValue);
    handleReset();
    setOpen(false);
  };

  const handleInsertExisting = (secretName: string) => {
    onInsertSecret(secretName);
    setOpen(false);
  };

  return (
    <Popover
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          handleReset();
        }
      }}
      open={open}
    >
      <PopoverTrigger
        render={
          <Button
            className="shrink-0"
            disabled={disabled}
            size="icon"
            type="button"
            variant="ghost"
          />
        }
      >
        <Key className="size-4" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96" side="top">
        <div className="space-y-4">
          <div>
            <h4 className="mb-1 font-semibold text-sm">Insert Header Secret</h4>
            <p className="text-muted-foreground text-xs">
              Create a new secret or select an existing one to insert at your
              cursor position
            </p>
          </div>

          <div className="space-y-2">
            <Input
              onChange={(e) => setNewSecretName(e.target.value)}
              placeholder="Secret name (e.g., API_TOKEN)"
              value={newSecretName}
            />
            <div className="relative">
              <Input
                className="pr-10"
                onChange={(e) => setNewSecretValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newSecretName.trim()) {
                    e.preventDefault();
                    handleCreateAndInsert();
                  }
                }}
                placeholder="Secret value (optional)"
                type={showNewSecretValue ? "text" : "password"}
                value={newSecretValue}
              />
              <Button
                className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowNewSecretValue(!showNewSecretValue)}
                size="sm"
                type="button"
                variant="link"
              >
                {showNewSecretValue ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
            </div>
            <Button
              className="w-full"
              disabled={!newSecretName.trim()}
              onClick={handleCreateAndInsert}
              size="sm"
              type="button"
            >
              <PlusCircle className="mr-2 size-4" />
              Create & Insert Secret
            </Button>
          </div>

          {availableSecrets.length > 0 && (
            <div className="space-y-2">
              <p className="font-medium text-muted-foreground text-xs">
                Or select existing secret:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {availableSecrets.map((secretName) => (
                  <Button
                    className="h-auto px-2.5 py-1 font-mono text-xs"
                    key={secretName}
                    onClick={() => handleInsertExisting(secretName)}
                    size="sm"
                    type="button"
                    variant="secondary"
                  >
                    {secretName}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
